import { produce } from 'immer'
import { Reducer, Action } from 'redux'
import {
    LoggedUser,
    Vehicle,
    CustomNotification,
    VehiclePhoto,
    Category,
    VehicleVideo,
    Stand,
    Company,
    Financial,
    CompanyFinancial,
} from '../shared/shared.models'
import { Sale } from '../sales/sales.interfaces'

type AppAction = Action<string> & { payload?: any }
export const AdminActions = {
    types: {
        LOGIN_SUCCESS: 'LOGIN_SUCCESS',
        LOGOUT: 'LOGOUT',
        GET_USERS_SUCCESS: 'GET_USERS_SUCCESS',
        GET_VEHICLES_SUCCESS: 'GET_VEHICLES_SUCCESS',
        CREATE_VEHICLE_SUCCESS: 'CREATE_VEHICLE_SUCCESS',
        UPDATE_VEHICLE_SUCCESS: 'UPDATE_VEHICLE_SUCCESS',
        UPDATE_VEHICLE_PHOTOS: 'UPDATE_VEHICLE_PHOTOS',
        GET_NOTIFICATIONS_SUCCESS: 'GET_NOTIFICATIONS_SUCCESS',
        UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION',
        GET_CATEGORIES_SUCCESS: 'GET_CATEGORIES_SUCCESS',
        GET_COST_TYPES_SUCCESS: 'GET_COST_TYPES_SUCCESS',
        DELETE_NOTIFICATION_SUCCESS: 'DELETE_NOTIFICATION_SUCCESS',
        DELETE_ALL_NOTIFICATION_SUCCESS: 'DELETE_ALL_NOTIFICATION_SUCCESS',
        UPDATE_SALE_SUCCESS: 'UPDATE_SALE_SUCCESS',
        UPDATE_VEHICLE_VIDEO: 'UPDATE_VEHICLE_VIDEO',
        GET_STANDS_SUCCESS: 'GET_STANDS_SUCCESS',
        CREATE_STAND_SUCCESS: 'CREATE_STAND_SUCCESS',
        UPDATE_STAND_SUCCESS: 'UPDATE_STAND_SUCCESS',
        DEACTIVATE_STAND_SUCCESS: 'DEACTIVATE_STAND_SUCCESS',
        GET_COMPANIES_SUCCESS: 'GET_COMPANIES_SUCCESS',
        CREATE_COMPANY_SUCCESS: 'CREATE_COMPANY_SUCCESS',
        UPDATE_COMPANY_SUCCESS: 'UPDATE_COMPANY_SUCCESS',
        DELETE_COMPANY_SUCCESS: 'DELETE_COMPANY_SUCCESS',
        UPDATE_COMPANY_LOGO: 'UPDATE_COMPANY_LOGO',
        CREATE_USER_SUCCESS: 'CREATE_USER_SUCCESS',
        UPDATE_USER_SUCCESS: 'UPDATE_USER_SUCCESS',
        ACTIVATE_USER_SUCCESS: 'ACTIVATE_USER_SUCCESS',
        DEACTIVATE_USER_SUCCESS: 'DEACTIVATE_USER_SUCCESS',
        GET_FINANCIALS_SUCCESS: 'GET_FINANCIALS_SUCCESS',
    },
    methods: {
        loginSuccessAction: (
            user: LoggedUser,
            token: string,
            company: Company
        ) => ({
            type: AdminActions.types.LOGIN_SUCCESS,
            payload: {
                user,
                token,
                company,
            },
        }),
        logoutAction: () => ({
            type: AdminActions.types.LOGOUT,
        }),
        getUsersSuccessAction: (users: LoggedUser[], companyUuid?: string) => ({
            type: AdminActions.types.GET_USERS_SUCCESS,
            payload: {
                users,
                companyUuid,
            },
        }),
        getVehiclesSuccessAction: (vehicles: Vehicle[]) => ({
            type: AdminActions.types.GET_VEHICLES_SUCCESS,
            payload: {
                vehicles,
            },
        }),
        deleteAllNotificationsSuccess: () => ({
            type: AdminActions.types.DELETE_ALL_NOTIFICATION_SUCCESS,
        }),
        createVehicleSuccessAction: (vehicle: Vehicle) => ({
            type: AdminActions.types.CREATE_VEHICLE_SUCCESS,
            payload: {
                vehicle,
            },
        }),
        updateVehicleSuccessAction: (vehicle: Vehicle) => ({
            type: AdminActions.types.UPDATE_VEHICLE_SUCCESS,
            payload: {
                vehicle,
            },
        }),
        updateVehiclePhotosSuccessAction: (
            vehicleId: string,
            photos: Array<VehiclePhoto>
        ) => ({
            type: AdminActions.types.UPDATE_VEHICLE_PHOTOS,
            payload: {
                vehicleId,
                photos,
            },
        }),
        getNotificationsSuccessAction: (
            notifications: CustomNotification[]
        ) => ({
            type: AdminActions.types.GET_NOTIFICATIONS_SUCCESS,
            payload: {
                notifications,
            },
        }),
        updateNotificationAction: (
            notificationId: string,
            newValue: boolean
        ) => ({
            type: AdminActions.types.UPDATE_NOTIFICATION,
            payload: {
                notificationId,
                newValue,
            },
        }),
        getCategoriesSuccessAction: (categories: Category[]) => ({
            type: AdminActions.types.GET_CATEGORIES_SUCCESS,
            payload: {
                categories,
            },
        }),
        getCostTypesSuccessAction: (costTypes: string[]) => ({
            type: AdminActions.types.GET_COST_TYPES_SUCCESS,
            payload: {
                costTypes,
            },
        }),
        deleteNotificationSuccessAction: (notificationId: string) => ({
            type: AdminActions.types.DELETE_NOTIFICATION_SUCCESS,
            payload: {
                notificationId,
            },
        }),
        updateSaleSuccessAction: (sale: Sale) => ({
            type: AdminActions.types.UPDATE_SALE_SUCCESS,
            payload: {
                sale,
            },
        }),
        updateVehicleVideoSuccessAction: (
            vehicleId: string,
            video: VehicleVideo
        ) => ({
            type: AdminActions.types.UPDATE_VEHICLE_VIDEO,
            payload: {
                vehicleId,
                video,
            },
        }),
        getStandsSuccessAction: (stands: Stand[]) => ({
            type: AdminActions.types.GET_STANDS_SUCCESS,
            payload: {
                stands,
            },
        }),
        createStandSuccessAction: (stand: Stand) => ({
            type: AdminActions.types.CREATE_STAND_SUCCESS,
            payload: {
                stand,
            },
        }),
        updateStandSuccessAction: (stand: Stand) => ({
            type: AdminActions.types.UPDATE_STAND_SUCCESS,
            payload: {
                stand,
            },
        }),
        deactivateStandSuccessAction: (stand: Stand) => ({
            type: AdminActions.types.DEACTIVATE_STAND_SUCCESS,
            payload: {
                stand,
            },
        }),
        getCompaniesSuccessAction: (companies: Company[]) => ({
            type: AdminActions.types.GET_COMPANIES_SUCCESS,
            payload: {
                companies,
            },
        }),
        createCompanySuccessAction: (company: Company) => ({
            type: AdminActions.types.CREATE_COMPANY_SUCCESS,
            payload: {
                company,
            },
        }),
        updateCompanySuccessAction: (company: Company) => ({
            type: AdminActions.types.UPDATE_COMPANY_SUCCESS,
            payload: {
                company,
            },
        }),
        deleteCompanySuccessAction: (companyId: string) => ({
            type: AdminActions.types.DELETE_COMPANY_SUCCESS,
            payload: {
                companyId,
            },
        }),
        updateLogoSuccessAction: (companyId: string, photo: string) => ({
            type: AdminActions.types.UPDATE_COMPANY_LOGO,
            payload: {
                companyId,
                photo,
            },
        }),
        createUserSuccessAction: (user: LoggedUser) => ({
            type: AdminActions.types.CREATE_USER_SUCCESS,
            payload: {
                user,
            },
        }),
        updateUserSuccessAction: (user: LoggedUser) => ({
            type: AdminActions.types.UPDATE_USER_SUCCESS,
            payload: {
                user,
            },
        }),
        activateUserSuccessAction: (user: LoggedUser) => ({
            type: AdminActions.types.ACTIVATE_USER_SUCCESS,
            payload: {
                user,
            },
        }),
        deactivateUserSuccessAction: (user: LoggedUser) => ({
            type: AdminActions.types.DEACTIVATE_USER_SUCCESS,
            payload: {
                user,
            },
        }),
        getFinancialsSuccessAction: (financials: Financial[]) => ({
            type: AdminActions.types.GET_FINANCIALS_SUCCESS,
            payload: {
                financials,
            },
        }),
    },
}

export interface AdminReducerInterface {
    user?: LoggedUser
    token?: string
    vehicles: Vehicle[]
    categories: Category[]
    users: LoggedUser[]
    // uuid of the company draft.users was last populated for (via
    // GET_USERS_SUCCESS), so actions that mutate draft.users in place can
    // tell whether the currently-loaded list actually belongs to them,
    // instead of guessing from draft.users[0].
    usersCompanyUuid?: string
    costTypes: string[]
    notifications: CustomNotification[]
    stands: Stand[]
    companies: Company[]
    company?: Company
    financials?: CompanyFinancial[]
}

export const initialState: AdminReducerInterface = {
    user: undefined,
    vehicles: [],
    categories: [],
    users: [],
    usersCompanyUuid: undefined,
    costTypes: [],
    notifications: [],
    stands: [],
    companies: [],
    company: undefined,
    financials: [],
}

export const adminReducer: Reducer<AdminReducerInterface, AppAction> = produce(
    (draft: AdminReducerInterface, action: AppAction) => {
        switch (action.type) {
            case AdminActions.types.LOGIN_SUCCESS: {
                const { user, token, company } = action.payload

                // Lets remove the notifications key from the user since this is re-rendering everything in our app
                const notifications = user.notifications || []
                delete user.notifications
                draft.notifications = notifications
                draft.user = user
                draft.token = token
                draft.company = company ? company : {}
                if (company) draft.financials = company.financials
                const financials = company ? company.financials : []
                if (company && company.financials) delete company.financials

                localStorage.setItem('token', token)
                localStorage.setItem('user', JSON.stringify(user))
                localStorage.setItem('company', JSON.stringify(company))
                localStorage.setItem('financials', JSON.stringify(financials))

                return
            }
            case AdminActions.types.LOGOUT:
                return initialState
            case AdminActions.types.GET_USERS_SUCCESS:
                const { users, companyUuid } = action.payload
                draft.users = users
                draft.usersCompanyUuid = companyUuid
                return
            case AdminActions.types.GET_VEHICLES_SUCCESS:
                draft.vehicles = action.payload.vehicles
                localStorage.setItem(
                    'vehicles',
                    JSON.stringify(action.payload.vehicles)
                )
                return
            case AdminActions.types.CREATE_VEHICLE_SUCCESS:
                const { vehicle } = action.payload
                draft.vehicles.push(vehicle)
                return
            case AdminActions.types.UPDATE_VEHICLE_SUCCESS: {
                const { vehicle } = action.payload
                const vehicleIdx = draft.vehicles.findIndex(
                    (v) => v.uuid === vehicle.uuid
                )
                if (vehicleIdx > -1) {
                    draft.vehicles.splice(vehicleIdx, 1, vehicle)
                } else {
                    draft.vehicles.push(vehicle)
                }
                return
            }
            case AdminActions.types.UPDATE_VEHICLE_PHOTOS: {
                const { vehicleId, photos } = action.payload
                const vehicleIdx = draft.vehicles.findIndex(
                    (v) => v.uuid === vehicleId
                )
                if (vehicleIdx > -1) {
                    draft.vehicles[vehicleIdx].photos = photos
                }
                return
            }
            case AdminActions.types.GET_NOTIFICATIONS_SUCCESS:
                const { notifications } = action.payload
                draft.notifications = notifications

                return
            case AdminActions.types.UPDATE_NOTIFICATION:
                const { notificationId, newValue } = action.payload
                const notifIdx = draft.notifications.findIndex(
                    (n) => n.uuid === notificationId
                )
                if (notifIdx > -1) {
                    draft.notifications[notifIdx].isRead = newValue
                }
                return
            case AdminActions.types.GET_CATEGORIES_SUCCESS:
                draft.categories = action.payload.categories
                localStorage.setItem(
                    'categories',
                    JSON.stringify(draft.categories)
                )
                return
            case AdminActions.types.DELETE_NOTIFICATION_SUCCESS:
                draft.notifications = draft.notifications.filter(
                    (n) => n.notificationUuid !== action.payload.notificationId
                )
                return
            case AdminActions.types.DELETE_ALL_NOTIFICATION_SUCCESS:
                draft.notifications = []
                return
            case AdminActions.types.GET_COST_TYPES_SUCCESS:
                draft.costTypes = action.payload.costTypes
                return
            case AdminActions.types.UPDATE_SALE_SUCCESS: {
                const sale = action.payload.sale as Sale
                const vehicleId = sale.vehicleUuid
                const vehicle = draft.vehicles.find((v) => v.uuid === vehicleId)
                if (vehicle) {
                    vehicle.sale = sale
                }
                return
            }
            case AdminActions.types.UPDATE_VEHICLE_VIDEO: {
                const { vehicleId, video } = action.payload
                const vehicleIdx = draft.vehicles.findIndex(
                    (v) => v.uuid === vehicleId
                )
                if (vehicleIdx > -1) {
                    draft.vehicles[vehicleIdx].video = video
                }
                return
            }
            case AdminActions.types.GET_STANDS_SUCCESS: {
                draft.stands = action.payload.stands
                localStorage.setItem('stands', JSON.stringify(draft.stands))
                return
            }
            case AdminActions.types.CREATE_STAND_SUCCESS: {
                const { stand } = action.payload
                draft.stands.push(stand)
                return
            }
            case AdminActions.types.DEACTIVATE_STAND_SUCCESS: {
                const { stand } = action.payload
                const standIdx = draft.stands.findIndex(
                    (v) => v.uuid === stand.uuid
                )
                if (standIdx > -1) {
                    draft.stands.splice(standIdx, 1, stand)
                }
                return
            }
            case AdminActions.types.UPDATE_STAND_SUCCESS: {
                const { stand } = action.payload
                const standIdx = draft.stands.findIndex(
                    (v) => v.uuid === stand.uuid
                )
                if (standIdx > -1) {
                    draft.stands.splice(standIdx, 1, stand)
                }
                return
            }
            case AdminActions.types.GET_COMPANIES_SUCCESS:
                draft.companies = action.payload.companies
                return
            case AdminActions.types.CREATE_COMPANY_SUCCESS: {
                const { company } = action.payload
                draft.companies.push(company)
                return
            }
            case AdminActions.types.UPDATE_COMPANY_SUCCESS: {
                const { company } = action.payload
                const standIdx = draft.companies.findIndex(
                    (v) => v.uuid === company.uuid
                )
                if (standIdx > -1) {
                    draft.companies.splice(standIdx, 1, company)
                }
                return
            }
            case AdminActions.types.DELETE_COMPANY_SUCCESS: {
                const { companyId } = action.payload
                draft.companies = draft.companies.filter(
                    (c) => c.uuid !== companyId
                )
                return
            }
            case AdminActions.types.GET_FINANCIALS_SUCCESS: {
                draft.financials = action.payload.financials
                return
            }
            case AdminActions.types.CREATE_USER_SUCCESS: {
                // draft.users is whichever company's list was last fetched
                // (GET_USERS_SUCCESS), not necessarily the new user's own
                // company - only append if it actually belongs there, so a
                // create for company B can't corrupt company A's loaded list.
                // usersCompanyUuid is the explicit record of which company
                // that fetch was for; when it's unset (nothing fetched yet
                // this session) we fall back to appending, matching the old
                // "list is empty" behaviour.
                const { user } = action.payload
                const isSameCompanyAsLoadedList =
                    draft.usersCompanyUuid === undefined ||
                    draft.usersCompanyUuid === user.companyUuid
                if (isSameCompanyAsLoadedList) {
                    draft.users.push(user)
                }
                return
            }
            case AdminActions.types.UPDATE_USER_SUCCESS:
            case AdminActions.types.ACTIVATE_USER_SUCCESS:
            case AdminActions.types.DEACTIVATE_USER_SUCCESS: {
                const { user } = action.payload
                const userIdx = draft.users.findIndex(
                    (u) => u.uuid === user.uuid
                )
                if (userIdx > -1) {
                    draft.users.splice(userIdx, 1, user)
                }
                return
            }
            case AdminActions.types.UPDATE_COMPANY_LOGO: {
                const { companyId, photo } = action.payload
                if (draft.company && draft.company.uuid === companyId) {
                    draft.company.logo = photo
                }
                const companyIdx = draft.companies.findIndex(
                    (c) => c.uuid === companyId
                )
                if (companyIdx > -1) {
                    draft.companies[companyIdx].logo = photo
                }
                return
            }
            default:
                return draft
        }
    },
    initialState
)
